import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAdmin, isDriver } from '../utils/helper';
import { isUserAllowed, isFieldUser } from '../utils/roleHelpers';
import { MISC_LABELS } from '../constants/labels';
import useCompanyFeatures from '../hooks/useCompanyFeatures';

const NavIcon = ({ children }) => (
    <svg className="sb-nav-ic" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
export const icons = {
    overview: <><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></>,
    jobs: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
    sales: <><path d="M3 3v18h18" /><path d="M19 9l-5 5-4-4-3 3" /></>,
    clients: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></>,
    expenses: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
    payroll: <><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
    cashflow: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    reports: <path d="M18 20V10M12 20V4M6 20v-6" />,
    route: <><circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" /></>,
    services: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>,
    materials: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
};

const NavItem = ({ to, icon, label }) => (
    <NavLink to={to}>
        <li>
            <span className="nav-link cursor-pointer">
                <div className="px-2 py-2 rounded">
                    <NavIcon>{icon}</NavIcon>
                    {label}
                </div>
            </span>
        </li>
    </NavLink>
);

const Sidebar = ({ sidebarToggle, setSidebarToggle, currentUser }) => {

    const { t } = useTranslation();
    const { materialsEnabled } = useCompanyFeatures()

    const SubMenu = ({ title, menu = [] }) => {
        const [isExpand, setIsExpand] = useState(false)
        return (
            <li>
                <span onClick={() => setIsExpand(!isExpand)} className={`nav-link cursor-pointer ${isExpand ? 'expand' : ''}`}>
                    <div className="px-2 py-2 rounded">
                        <NavIcon>{icons.reports}</NavIcon>
                        <span>{title}</span>
                        <svg className="sb-nav-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isExpand ? <polyline points="6 9 12 15 18 9" /> : <polyline points="9 18 15 12 9 6" />}
                        </svg>
                    </div>
                </span>
                <Collapse in={isExpand}>
                    <ul className="sub-menu my-2">
                        {menu.map((m, index) =>
                            <NavLink key={`menu-${title}-${index}`} to={`/reports/${m.path}`}>
                                <li className="nav-link my-2">
                                    <span className="sb-sub-dot" aria-hidden="true" />
                                    {m.title}
                                </li>
                            </NavLink>
                        )}
                    </ul>
                </Collapse>
            </li>
        )
    }

    const showMain     = isUserAllowed('/home') || isUserAllowed('/jobs') || isUserAllowed('/sales') || isUserAllowed('/clients')
    const showFinance  = isUserAllowed('/expenses') || isUserAllowed('/payrolls') || isUserAllowed('/cash-flow')
    const showWorkspace= isUserAllowed('/calendar') || isUserAllowed('/route-plan') || isUserAllowed('/reports/job-reports') || isUserAllowed('/services') || isUserAllowed('/materials')

    return (
        <nav id="sidebar" className={`col-md-3 col-lg-2 sidebar px-0 ${sidebarToggle ? 'expanded' : ''}`}>
            <div id="sidebar-close" onClick={() => setSidebarToggle(false)} className="sidebar-close d-block d-md-none px-3">
                <i className="fas fa-times text-muted"></i>
            </div>
            <div className="sidebar-sticky">

                <div className="sidebar-top">
                    <div className="center-xy p-3">
                        <Link to='/'><img className="logo-img px-2" src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt={t('nav.sorobyLogo')} /></Link>
                    </div>
                </div>

                <ul className="nav flex-column mb-3">

                    {/* ── MAIN ── */}
                    {showMain && <li className="sb-nav-section">{t('nav.main')}</li>}
                    {isUserAllowed('/home')    && <NavItem to="/home"    icon={icons.overview} label={t('nav.overview')} />}
                    {isUserAllowed('/jobs')    && <NavItem to="/jobs"    icon={icons.jobs}     label={t('nav.jobs')} />}
                    {isUserAllowed('/sales')   && <NavItem to="/sales"   icon={icons.sales}    label={t('nav.sales')} />}
                    {isUserAllowed('/clients') && <NavItem to="/clients" icon={icons.clients}  label={t('nav.clients')} />}

                    {/* ── FINANCE ── */}
                    {showFinance && <li className="sb-nav-section">{t('nav.finance')}</li>}
                    {isUserAllowed('/expenses')  && <NavItem to="/expenses"  icon={icons.expenses} label={t('nav.expenses')} />}
                    {isUserAllowed('/payrolls')  && <NavItem to="/payrolls"  icon={icons.payroll}  label={t('nav.payroll')} />}
                    {isUserAllowed('/cash-flow') && <NavItem to="/cash-flow" icon={icons.cashflow} label={t('nav.cashFlow')} />}

                    {/* ── WORKSPACE ── */}
                    {showWorkspace && <li className="sb-nav-section">{t('nav.workspace')}</li>}
                    {isUserAllowed('/calendar') && <NavItem to="/calendar" icon={icons.calendar} label={t('nav.calendar')} />}
                    {isUserAllowed('/route-plan') && <NavItem to="/route-plan" icon={icons.route} label={isFieldUser() ? MISC_LABELS.myRoute : MISC_LABELS.routePlan} />}
                    {isUserAllowed('/reports/job-reports') &&
                        <SubMenu title={t('nav.reports')} menu={[
                            { title: t('reports.jobsHistory'),    path: 'jobs-history' },
                            { title: t('reports.jobsPerformance'),path: 'jobs-performance' },
                            { title: t('reports.assignees'),       path: 'jobs-assignees' },
                            { title: t('reports.services'),        path: 'services-reports' },
                            { title: t('reports.expenses'),        path: 'expenses' },
                            ...(materialsEnabled ? [{ title: t('reports.materials'), path: 'materials' }] : []),
                        ]} />
                    }
                    {isUserAllowed('/services') && <NavItem to="/services" icon={icons.services} label={t('nav.services')} />}
                    {materialsEnabled && isUserAllowed('/materials') && <NavItem to="/materials" icon={icons.materials} label={t('nav.materials')} />}

                </ul>

                {isAdmin() &&
                    <div className={`sidebar-bottom col-lg-2 col-md-3 py-3 ${sidebarToggle ? 'expanded' : ''}`}>
                        <ul className="nav flex-column">
                            <NavItem to="/settings" icon={icons.settings} label={t('nav.settings')} />
                        </ul>
                    </div>
                }

            </div>
        </nav>
    )
}

export default Sidebar;
